import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import axios from "axios";
import { toast } from "react-toastify";
import { getDefaultAxiosConfig } from "../../../../utilities/requests";
import { getRightworkAPI, postRightworkAPI } from "../../requests/utilities/requests";
import {
  API_get_store_team_members,
  API_get_role_names,
  API_get_plan_tasks,
  API_get_plan_tasks_for_date,
  API_validate_plan_task_assignment,
  API_assign_plan_task,
  API_save_plan_task_assignment,
  API_get_plan_task_follow_ups,
  API_create_plan_task_follow_up,
  API_link_plan_task_follow_up,
  API_post_maintenance_request,
  API_get_store_employees_for_assignment,
} from "../../../../constants";
import TaskDetailDrawer from "./TaskDetailDrawer";
import AssignMemberModal from "./AssignMemberModal";
import { FollowUpTaskModal, ReauditModal } from "./FollowUpModals";
import MaintenanceTicketDrawer from "../../maintenance/MaintenanceTicketDrawer";
import useTicketOptions from "../../maintenance/useTicketOptions";
import { formatQhnum, rollupTask, memberMatchesAudiencePermission } from "./taskHubUtils";

// The task detail drawer and everything it opens (assign picker, validation
// dialog, the checklist modal inside the drawer), owned in one place so every
// manager surface that lists occurrences (the Tasks hub, Live Action) opens
// the same drawer with the same actions. The host fetches the day payload
// the drawer needs, the roster the assign picker needs, and the task library
// that names each task's eligible roles; a parent that already has the
// library passes it in and skips that fetch.
//
//   const host = React.useRef(null);
//   <TaskDrawerHost ref={host} storeId=... homeCompany=... canManage
//                   onChanged={refetchMyLists} />
//   host.current.open(taskUuid, "2026-09-07", slotIndex, "details");
//
// onChanged fires after any action that changed an occurrence (assign,
// validate, return, save, submit, comment), after the drawer's own day
// payload has been refetched.

const validateBoxStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 460,
  maxWidth: "92vw",
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
  outline: "none",
};

const memberRoleIds = (m) => {
  const ids = new Set();
  if (m.primary_role_id != null) ids.add(m.primary_role_id);
  (m.roles_and_role_types || m.roles || []).forEach((r) => {
    const id = r?.role ?? r?.scheduled_role ?? r?.role_id ?? r?.id;
    if (id != null) ids.add(id);
  });
  return ids;
};

const TaskDrawerHost = React.forwardRef(function TaskDrawerHost(props, ref) {
  const { storeId, storeName, homeCompany, canManage, tz, todayStr, onChanged } = props;
  // The ticket drawer gates its fields on the role; a manager surface that
  // does not pass one is a manager.
  const permissionRole = props.permissionRole || (canManage ? "company_manager" : "company_employee");

  // {uuid, date, slotIndex, tab} while the drawer is open.
  const [drawerTarget, setDrawerTarget] = React.useState(null);
  const [drawerDay, setDrawerDay] = React.useState(null); // {store, date, tasks}
  // A failed detail fetch is its own state: without it a null task reads as
  // "still loading" and the drawer spins forever with no way back.
  const [drawerError, setDrawerError] = React.useState(false);
  // {slotIndex, selectedIds} while the assign picker is open; slotIndex null
  // = the whole (non-intraday) occurrence.
  const [assignTarget, setAssignTarget] = React.useState(null);
  const [validateRow, setValidateRow] = React.useState(null);
  const [validateNote, setValidateNote] = React.useState("");
  // Scored audits: what the open occurrence led to, and the dialogs that
  // raise more. followUpBusy guards a double submit.
  const [followUps, setFollowUps] = React.useState([]);
  const [followUpTaskFor, setFollowUpTaskFor] = React.useState(null); // occurrence
  const [reauditFor, setReauditFor] = React.useState(null); // occurrence
  const [ticketFor, setTicketFor] = React.useState(null); // {occ, psa}
  const [followUpBusy, setFollowUpBusy] = React.useState(false);
  const [ticketPeople, setTicketPeople] = React.useState({ employees: [], approvers: [] });
  const ticketOptions = useTicketOptions(storeId, "maintenance", !!ticketFor);

  const [storeTeamMembers, setStoreTeamMembers] = React.useState([]);
  const [ownRoleNames, setOwnRoleNames] = React.useState([]);
  const [ownPlanTasks, setOwnPlanTasks] = React.useState([]);
  const roleNames = props.roleNames || ownRoleNames;
  const planTasks = props.planTasks || ownPlanTasks;

  // ---------- fetches ----------

  // A roster or library that arrives after a store switch belongs to the old
  // store; the refs let a slow response be dropped.
  const latestStoreRef = React.useRef(storeId);
  latestStoreRef.current = storeId;

  React.useEffect(() => {
    let cancelled = false;
    setStoreTeamMembers([]);
    setDrawerTarget(null);
    setDrawerDay(null);
    setDrawerError(false);
    setAssignTarget(null);
    setValidateRow(null);
    if (!storeId) return undefined;
    (async () => {
      try {
        const axiosConfig = await getDefaultAxiosConfig();
        const response = await axios.get(`${API_get_store_team_members}${storeId}`, axiosConfig);
        if (!cancelled && latestStoreRef.current === storeId) setStoreTeamMembers(response.data || []);
      } catch (error) {
        console.error("Error fetching store team members:", error);
      }
      if (props.roleNames) return;
      try {
        const axiosConfig = await getDefaultAxiosConfig();
        const response = await axios.get(`${API_get_role_names}${storeId}`, axiosConfig);
        if (!cancelled && latestStoreRef.current === storeId) setOwnRoleNames(response.data || []);
      } catch (error) {
        console.error("Error fetching role names:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  React.useEffect(() => {
    if (props.planTasks || !homeCompany) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getRightworkAPI(API_get_plan_tasks, { company_id: homeCompany });
        if (cancelled) return;
        setOwnPlanTasks(res.status === 200 ? res.data?.redux?.payload?.plan_tasks || [] : []);
      } catch (error) {
        console.error("Error fetching task library:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeCompany, !!props.planTasks]);

  // Drawer payloads carry their store so a response from the previous store
  // (or an in-flight one during a switch) never renders under the new one.
  const drawerContextRef = React.useRef(null);
  const fetchDrawerDay = React.useCallback(
    async (date) => {
      const store = storeId;
      const context = `${store}:${date}`;
      drawerContextRef.current = context;
      setDrawerError(false);
      try {
        const axiosConfig = await getDefaultAxiosConfig();
        const response = await axios.get(
          `${API_get_plan_tasks_for_date}?store_id=${store}&date=${date}`,
          axiosConfig
        );
        // Superseded by a newer fetch, which owns the outcome now.
        if (drawerContextRef.current !== context) return true;
        setDrawerDay({ store, date, tasks: response.data?.redux?.payload?.plan_tasks || [] });
        return true;
      } catch (error) {
        console.error("Error fetching task day:", error);
        if (drawerContextRef.current === context) setDrawerError(true);
        return false;
      }
    },
    [storeId]
  );

  const drawerTargetRef = React.useRef(drawerTarget);
  drawerTargetRef.current = drawerTarget;

  const open = React.useCallback(
    (uuid, date, slotIndex = null, tab = "details") => {
      setDrawerTarget({ uuid, date, slotIndex, tab });
      fetchDrawerDay(date);
    },
    [fetchDrawerDay]
  );
  const close = React.useCallback(() => {
    setDrawerTarget(null);
    setDrawerDay(null);
  }, []);
  // Resolves false when the drawer's day could not be refetched.
  const refresh = React.useCallback(async () => {
    const target = drawerTargetRef.current;
    return target?.date ? fetchDrawerDay(target.date) : true;
  }, [fetchDrawerDay]);

  React.useImperativeHandle(ref, () => ({ open, close, refresh }), [open, close, refresh]);

  // Refresh this drawer's own payload first, then let the parent refetch its
  // lists, so the drawer never shows a state older than the row behind it.
  const afterChange = React.useCallback(async () => {
    const refreshed = await refresh();
    // A parent that reports nothing (undefined) is not a failure.
    const parentRefreshed = (await onChanged?.()) !== false;
    return refreshed && parentRefreshed;
  }, [refresh, onChanged]);

  // ---------- derived ----------

  // The day payload does not include each task's eligible roles; the library
  // payload does. Join them so the drawer and assign picker can name roles.
  const rolesByTaskUuid = React.useMemo(() => {
    const map = {};
    (planTasks || []).forEach((t) => {
      map[t.uuid] = t.auto_assignable_roles || [];
    });
    return map;
  }, [planTasks]);

  // The day this drawer needs has come back. A day that arrived without the
  // task still counts: the occurrence is gone, which is an answer, not a wait.
  const drawerDayReady = !!(
    drawerTarget && drawerDay && drawerDay.date === drawerTarget.date && drawerDay.store === storeId
  );
  // Memoized on the payload: the checklist modal resets its edits whenever
  // the task object it was given changes identity, so a parent re-render
  // (a poll, a clock tick) must not hand it a fresh copy.
  const drawerTask = React.useMemo(() => {
    if (!drawerDayReady) return null;
    const t = (drawerDay.tasks || []).find((x) => x.uuid === drawerTarget.uuid);
    if (!t) return null;
    return t.auto_assignable_roles
      ? t
      : { ...t, auto_assignable_roles: rolesByTaskUuid[t.uuid] || [] };
  }, [drawerDayReady, drawerDay, drawerTarget, rolesByTaskUuid]);
  const drawerRollup = drawerTask ? rollupTask(drawerTask) : null;

  // The follow-ups of a submitted scored occurrence, refetched with the day
  // payload so a ticket or task raised elsewhere shows up on the next open.
  const scoredOccUuid =
    drawerRollup?.primaryOcc?.submitted_at && drawerRollup.primaryOcc.score_total != null
      ? drawerRollup.primaryOcc.uuid
      : null;

  // The audit the list on screen belongs to: a response for an audit the
  // manager has since closed or switched away from is dropped, and the
  // list is cleared the moment a new audit is fetched.
  const followUpsForRef = React.useRef(null);
  const fetchFollowUps = React.useCallback(async () => {
    followUpsForRef.current = scoredOccUuid;
    setFollowUps([]);
    if (!scoredOccUuid) return;
    try {
      const res = await getRightworkAPI(API_get_plan_task_follow_ups, {
        store_id: storeId,
        plan_task_assignment_uuid: scoredOccUuid,
      });
      if (followUpsForRef.current !== scoredOccUuid) return;
      setFollowUps(res.status === 200 ? res.data?.redux?.payload?.follow_ups || [] : []);
    } catch (error) {
      console.error("Error fetching audit follow-ups:", error);
    }
  }, [scoredOccUuid, storeId]);

  React.useEffect(() => {
    fetchFollowUps();
  }, [fetchFollowUps, drawerDay]);
  // The audit on screen and its fetch, for a callback that may outlive the
  // render it was made in (the retry toast below).
  const scoredOccRef = React.useRef(null);
  scoredOccRef.current = scoredOccUuid;
  const fetchFollowUpsRef = React.useRef(null);
  fetchFollowUpsRef.current = fetchFollowUps;

  // The assignee and approver pools for a ticket at this store, fetched the
  // first time the ticket drawer opens here (the same call the Tickets page
  // makes).
  React.useEffect(() => {
    if (!ticketFor || !storeId) return undefined;
    let cancelled = false;
    (async () => {
      const base = { store_id: storeId, ticket_store_id: storeId, category: "maintenance" };
      try {
        const [emp, appr] = await Promise.all([
          getRightworkAPI(API_get_store_employees_for_assignment, base),
          getRightworkAPI(API_get_store_employees_for_assignment, { ...base, role: "approver" }),
        ]);
        if (cancelled) return;
        setTicketPeople({
          employees: emp.data?.redux?.payload?.employees || [],
          approvers: appr.data?.redux?.payload?.employees || [],
        });
      } catch (error) {
        // Non-critical: the drawer works without assignee pools.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!ticketFor, storeId]);

  // People who can be assigned: the roster, narrowed to the task's roles when
  // it names any (the backend eligibility rule), minus anyone a manager has
  // hidden. Hiding someone is a statement that they are not working here now,
  // so they do not belong in a list of people to hand work to.
  const eligibleMembers = React.useMemo(() => {
    const roleIds = drawerTask?.auto_assignable_roles || [];
    const picks = drawerTask?.audience_permission_types || [];
    return (storeTeamMembers || []).filter((m) => {
      if (m.is_archived || m.is_hidden) return false;
      if (m.permission_type === "company_timeclock") return false;
      if (!roleIds.length && !picks.length) return true;
      const mine = memberRoleIds(m);
      return roleIds.some((id) => mine.has(id)) || memberMatchesAudiencePermission(m, drawerTask);
    });
  }, [storeTeamMembers, drawerTask]);


  // ---------- actions ----------

  const showError = (error, fallback = "Request failed") => {
    toast.dismiss();
    toast.error(error?.response?.data?.system?.message || fallback, { autoClose: 4000 });
  };

  const postTransition = async (url, row, extra = {}, successMessage) => {
    try {
      toast.loading("Saving...", { autoClose: false });
      const res = await postRightworkAPI(url, {
        store_id: storeId,
        plan_task_assignment_uuid: row.plan_task_assignment_uuid || row.uuid,
        specific_date: row.date,
        ...extra,
      });
      toast.dismiss();
      if (res.status === 200) {
        if (successMessage) toast.success(successMessage, { autoClose: 2500 });
        await afterChange();
        return true;
      }
    } catch (error) {
      showError(error);
    }
    return false;
  };

  const submitValidation = async (decision) => {
    if (!validateRow) return;
    if (decision === "return" && !validateNote.trim()) {
      toast.error("Add a note so the team knows what to change.", { autoClose: 3000 });
      return;
    }
    const ok = await postTransition(
      API_validate_plan_task_assignment,
      validateRow,
      { decision, note: validateNote },
      decision === "return" ? "Returned for rework" : "Validated"
    );
    if (ok) setValidateRow(null);
  };

  // Complete a checklist from the drawer (managers may finish anything at their store).
  // Resolves true once saved, false on failure (the modal stays open).
  const save_plan_task_assignment = async (plan_task_assignment_uuid, plan_subtask_assignments, is_submit) => {
    try {
      toast.loading(is_submit ? "Submitting..." : "Saving...", { autoClose: false });
      const res = await postRightworkAPI(API_save_plan_task_assignment, {
        store_id: storeId,
        plan_task_assignment_uuid,
        plan_subtask_assignments: JSON.stringify(plan_subtask_assignments),
        is_submit,
        specific_date: drawerTargetRef.current?.date,
      });
      toast.dismiss();
      if (res.status === 200) {
        if (!(await afterChange())) {
          toast.error("Saved, but the task could not refresh. Check your connection.", { autoClose: 4000 });
          return false;
        }
        toast.success(is_submit ? "Submitted" : "Saved", { autoClose: 2500 });
        return true;
      }
    } catch (error) {
      showError(error);
    }
    return false;
  };

  // ---------- audit follow-ups ----------

  const createFollowUp = async (occ, body, successMessage) => {
    setFollowUpBusy(true);
    try {
      toast.loading("Saving...", { autoClose: false });
      const res = await postRightworkAPI(API_create_plan_task_follow_up, {
        company_id: homeCompany,
        plan_task_assignment_uuid: occ.uuid,
        ...body,
      });
      toast.dismiss();
      if (res.status === 200) {
        const created = res.data?.redux?.payload?.plan_task;
        toast.success(created?.number ? `${successMessage} #${created.number}` : successMessage, { autoClose: 3000 });
        setFollowUpTaskFor(null);
        setReauditFor(null);
        await fetchFollowUps();
        await onChanged?.();
        return true;
      }
    } catch (error) {
      showError(error);
    } finally {
      setFollowUpBusy(false);
    }
    return false;
  };

  // The ticket drawer posts through here: create the ticket at this store,
  // then link it to the failed item it was raised from.
  const createTicketFromStep = async (formData) => {
    if (!ticketFor) return;
    if (formData.recurrence_interval) {
      toast.error("A recurring ticket can't be raised from an audit item. Create it from the Tickets page.", { autoClose: 4000 });
      throw new Error("recurring");
    }
    const { store_ids, ...ticketData } = formData;
    const res = await postRightworkAPI(API_post_maintenance_request, {
      store_id: storeId,
      ...ticketData,
    });
    const ticket = res.data?.redux?.payload?.maintenance_request;
    if (!ticket?.uuid) throw new Error("Ticket was not created");
    await linkTicket(ticket, ticketFor.occ.uuid, ticketFor.psa.uuid);
  };

  // Link an existing ticket to the failed item it was raised from. The
  // ticket exists whether or not this succeeds, so a failure keeps enough
  // to retry the link itself: never a second ticket.
  const linkTicket = async (ticket, occUuid, psaUuid) => {
    try {
      await postRightworkAPI(API_link_plan_task_follow_up, {
        company_id: homeCompany,
        plan_task_assignment_uuid: occUuid,
        plan_subtask_assignment_uuid: psaUuid,
        maintenance_request_uuid: ticket.uuid,
      });
      toast.success(`Ticket #${ticket.ticket_number} created`, { autoClose: 3000 });
      // Refresh only the audit the ticket belongs to: a retry pressed after
      // the manager moved on to another audit must not install this one's
      // links there (the next open of the source audit fetches them).
      if (scoredOccRef.current === occUuid) await fetchFollowUpsRef.current?.();
    } catch (error) {

      toast.dismiss();
      toast.warning(
        <span>
          Ticket #{ticket.ticket_number} was created but could not be linked to this item.{" "}
          <button
            type="button"
            className="underline font-semibold"
            onClick={() => linkTicket(ticket, occUuid, psaUuid)}
          >
            Retry the link
          </button>
        </span>,
        { autoClose: false, closeOnClick: false }
      );
    }
  };


  const ticketSeed = React.useMemo(() => {
    if (!ticketFor) return null;
    const { occ, psa } = ticketFor;
    // The item as it read when audited.
    const stampedItem = (occ?.audit_items || []).find((i) => i?.uuid === psa.plan_subtask?.uuid);
    const sub = stampedItem ? { ...(psa.plan_subtask || {}), name: stampedItem.name, description: stampedItem.description } : psa.plan_subtask || {};

    const taskName = drawerTask?.name || "Audit";
    const number = drawerTask?.number ? ` #${drawerTask.number}` : "";
    const where = storeName ? ` at ${storeName}` : "";
    const parts = [
      sub.description ? `Standard: ${sub.description}` : null,
      psa.value_text ? `Auditor's note: ${psa.value_text}` : null,
      `Raised from ${taskName}${number}${where}${occ.date ? ` on ${occ.date}` : ""}.`,
    ].filter(Boolean);
    return { title: sub.name || "", description: parts.join("\n\n") };
  }, [ticketFor, drawerTask, storeName]);

  const openAssign = (slotIndex, occ) => {
    const selectedIds = (occ?.plan_task_assignment_members || [])
      .filter((m) => !m.claimed)
      .map((m) => m.store_team_member?.id ?? m.store_team_member_id);
    setAssignTarget({ slotIndex, selectedIds });
  };

  const saveAssignment = async (ids) => {
    if (!drawerTask || !drawerTarget || !assignTarget) return;
    const payload = {
      store_id: storeId,
      plan_task_uuid: drawerTask.uuid,
      date: drawerTarget.date,
      assigned_members: JSON.stringify(ids.map((id) => ({ store_team_member_id: id }))),
    };
    // Intraday: one slot at a time. Omitting the index would pin every slot.
    if (assignTarget.slotIndex != null) payload.intraday_slot_index = assignTarget.slotIndex;
    try {
      toast.loading("Saving...", { autoClose: false });
      const res = await postRightworkAPI(API_assign_plan_task, payload);
      toast.dismiss();
      if (res.status === 200) {
        toast.success(ids.length ? "Assigned" : "Open to everyone eligible", { autoClose: 3000 });
        setAssignTarget(null);
        await afterChange();
      }
    } catch (error) {
      showError(error);
    }
  };

  return (
    <>
      <TaskDetailDrawer
        open={!!drawerTarget}
        storeId={storeId}
        initialTab={drawerTarget?.tab || "details"}
        onCommented={afterChange}
        tz={tz}
        // Only a drawer with nothing to show spins. A refetch of a day it
        // already has keeps the current content (and the checklist modal
        // over it) in place until the fresh payload lands.
        loading={!!drawerTarget && !drawerDayReady && !drawerError}
        error={drawerError}
        onRetry={() => drawerTarget?.date && fetchDrawerDay(drawerTarget.date)}
        task={drawerTask}
        rollup={drawerRollup}
        dateStr={drawerTarget?.date || todayStr}
        todayStr={todayStr}
        focusSlotIndex={drawerTarget?.slotIndex ?? null}
        roleNames={roleNames}
        homeCompany={homeCompany}
        canManage={canManage}
        onClose={close}
        onAssign={openAssign}
        onValidate={(occ) => {
          setValidateNote("");
          setValidateRow({ ...occ, plan_task_assignment_uuid: occ.uuid });
        }}
        save_plan_task_assignment={save_plan_task_assignment}
        followUps={followUps}
        onCreateTicket={(psa) => setTicketFor({ occ: drawerRollup?.primaryOcc, psa })}
        onCreateFollowUpTask={(occ) => setFollowUpTaskFor(occ)}
        onScheduleReaudit={(occ) => setReauditFor(occ)}
      />

      <FollowUpTaskModal
        open={!!followUpTaskFor}
        occ={followUpTaskFor}
        task={drawerTask}
        tz={tz}
        members={storeTeamMembers}
        busy={followUpBusy}
        onClose={() => setFollowUpTaskFor(null)}
        onConfirm={({ name, due_at, step_uuids, audience, assignee_ids }) =>
          createFollowUp(
            followUpTaskFor,
            {
              kind: "task",
              name,
              due_at,
              plan_subtask_assignment_uuids: JSON.stringify(step_uuids),
              audience,
              assignee_ids: JSON.stringify(assignee_ids || []),
            },
            "Follow-up task created"
          )
        }
      />


      <ReauditModal
        open={!!reauditFor}
        occ={reauditFor}
        task={drawerTask}
        tz={tz}
        busy={followUpBusy}
        onClose={() => setReauditFor(null)}
        onConfirm={({ due_at }) => createFollowUp(reauditFor, { kind: "reaudit", due_at }, "Re-audit scheduled")}
      />

      <MaintenanceTicketDrawer
        isOpen={!!ticketFor}
        onClose={() => setTicketFor(null)}
        ticket={null}
        initialValues={ticketSeed}
        category="maintenance"
        onSubmit={createTicketFromStep}
        permissionRole={permissionRole}
        permissionOverrides={props.permissionOverrides || []}
        homeCompany={homeCompany}
        homeStore={storeId}
        storeNames={storeId ? [{ id: storeId, store_name: storeName || "" }] : []}
        employees={ticketPeople.employees}
        approvers={ticketPeople.approvers}
        onStoreChange={() => {}}
        issueTypeOptions={ticketOptions.issueTypeOptions}
        priorityOptions={ticketOptions.priorityOptions}
        optionsStatus={ticketOptions.ticketOptionsStatus}
        onRetryOptions={ticketOptions.retryTicketOptions}
      />

      <AssignMemberModal

        open={!!assignTarget}
        task={drawerTask}
        slotLabel={
          assignTarget?.slotIndex != null && drawerTask
            ? formatQhnum(
                drawerTask.lower_bound_qhnum +
                  (assignTarget.slotIndex * (drawerTask.intraday_repeat_interval_minutes || 0)) / 60
              )
            : null
        }
        members={eligibleMembers}
        roleNames={roleNames}
        selectedIds={assignTarget?.selectedIds || []}
        onClose={() => setAssignTarget(null)}
        onSave={saveAssignment}
      />

      <Modal open={!!validateRow} onClose={() => setValidateRow(null)}>
        <Box sx={validateBoxStyle}>
          <h3 className="text-xl font-semibold text-slate-900">
            Validate {validateRow?.plan_task?.name ? `"${validateRow.plan_task.name}"` : "this task"}
          </h3>
          <p className="text-sm text-slate-500 mt-2">
            Validating marks the work complete. Returning it sends it back with your note so the team can fix it.
          </p>
          <TextField
            value={validateNote}
            onChange={(e) => setValidateNote(e.target.value)}
            placeholder="Note (required to return)"
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 2 }}
            InputProps={{
              sx: {
                backgroundColor: "white",
                borderRadius: "8px",
                fontSize: "14px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
              },
            }}
          />
          <div className="flex justify-end gap-2 mt-5">
            <button
              className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium"
              onClick={() => setValidateRow(null)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 rounded-lg border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 text-sm font-medium"
              onClick={() => submitValidation("return")}
            >
              Return with note
            </button>
            <button
              className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
              onClick={() => submitValidation("validate")}
            >
              Validate
            </button>
          </div>
        </Box>
      </Modal>
    </>
  );
});

export default TaskDrawerHost;
